import type {
  CreatePoiPayload,
  IPOI,
  IPoiCategory,
  PoiCategoryDTO,
  PoiCreateRequest,
  POIDTO,
  PoiType,
  PoiUpdateRequest,
  UpdatePoiPayload,
} from '@/shared/api/types/poi-types'
import { POI_CREATED_SOURCE } from '@/shared/constants/poi-created-source'
import type { PoiCreatedSource } from '@/shared/constants/poi-created-source'
import { POI_STATUS } from '@/shared/constants/poi-status'
import { POI_TYPES } from '@/shared/constants/poi-type'
import {
  baseTranslationsToLocalizedData,
  localizedDataToPoiTranslations,
} from './localized-data-mapper'
import { mapFlatToPosition, mapPositionToFlat } from './position-mapper'

function toPoiType(value: string | undefined): PoiType {
  if (value === POI_TYPES.MAJOR || value === POI_TYPES.MINOR) {
    return value
  }
  return POI_TYPES.MAJOR
}

function mapDeletedToStatus(isDeleted: boolean): IPOI['status'] {
  return isDeleted ? POI_STATUS.DELETED : POI_STATUS.PUBLISHED
}

function normalizeCreatedSource(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
}

function toPoiCreatedSource(value: string | null | undefined): PoiCreatedSource {
  if (value && normalizeCreatedSource(value) === POI_CREATED_SOURCE.CRAWL_DATA_SYSTEM) {
    return POI_CREATED_SOURCE.CRAWL_DATA_SYSTEM
  }
  return POI_CREATED_SOURCE.TENANT_ADMIN
}

export function mapPoiDtoToPoi(dto: POIDTO): IPOI {
  const position = mapPositionToFlat(dto.position)
  const translations = localizedDataToPoiTranslations(dto.localizedData)
  const categoryData =
    dto.categoryData && typeof dto.categoryData === 'object' && 'id' in dto.categoryData
      ? mapPoiCategoryDto(dto.categoryData as PoiCategoryDTO)
      : null

  return {
    id: dto.id,
    tenantId: dto.tenantId,
    ...(dto.tenantDestinationId ? { tenantDestinationId: dto.tenantDestinationId } : {}),
    destinationName: dto.destinationName ?? '',
    localizedData: translations,
    latitude: position.latitude,
    longitude: position.longitude,
    range: dto.range,
    type: toPoiType(dto.type),
    popupAd: dto.popupAd,
    category: categoryData?.id ?? dto.category ?? '',
    categoryData,
    ...(dto.address ? { address: dto.address } : {}),
    banner: dto.banner ?? [],
    status: mapDeletedToStatus(dto.isDeleted),
    isDeleted: dto.isDeleted,
    isActive: dto.isActive ?? !dto.isDeleted,
    audio: [],
    createdSource: toPoiCreatedSource(dto.createdSource),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

export function mapPoiCreatePayloadToRequest(payload: CreatePoiPayload): PoiCreateRequest {
  return {
    tenantDestinationId: payload.tenantDestinationId,
    localizedData: baseTranslationsToLocalizedData(payload.localizedData, { includeMeta: true }),
    position: mapFlatToPosition({ latitude: payload.latitude, longitude: payload.longitude }),
    type: toPoiType(payload.type),
    range: payload.range,
    popupAd: payload.popupAd,
    ...(payload.category !== undefined ? { category: payload.category } : {}),
    ...(payload.address !== undefined ? { address: payload.address } : {}),
    banner: payload.banner,
    ...(payload.isActive !== undefined ? { isActive: payload.isActive } : {}),
  }
}

export function mapPoiUpdatePayloadToRequest(payload: UpdatePoiPayload): PoiUpdateRequest {
  const request: PoiUpdateRequest = {}

  if (payload.tenantDestinationId !== undefined) {
    request.tenantDestinationId = payload.tenantDestinationId
  }

  if (payload.localizedData !== undefined) {
    request.localizedData = baseTranslationsToLocalizedData(payload.localizedData, {
      includeMeta: true,
    })
  }

  if (payload.latitude !== undefined || payload.longitude !== undefined) {
    request.position = mapFlatToPosition({
      latitude: payload.latitude ?? 0,
      longitude: payload.longitude ?? 0,
    })
  }

  if (payload.type !== undefined) {
    request.type = toPoiType(payload.type)
  }
  if (payload.range !== undefined) {
    request.range = payload.range
  }
  if (payload.isActive !== undefined) {
    request.isActive = payload.isActive
  }
  if (payload.popupAd !== undefined) {
    request.popupAd = payload.popupAd
  }

  if (payload.category !== undefined) {
    request.category = payload.category
  }
  if (payload.address !== undefined) {
    request.address = payload.address
  }
  if (payload.banner !== undefined) {
    request.banner = payload.banner
  }

  return request
}

export function mapPoiCategoryDto(dto: PoiCategoryDTO): IPoiCategory {
  const localizedData = dto.localizedData ?? {}
  return {
    id: dto.id,
    ...(dto.tenantId ? { tenantId: dto.tenantId } : {}),
    ...(dto.icon ? { icon: dto.icon } : {}),
    ...(dto.iconActive ? { iconActive: dto.iconActive } : {}),
    ...(dto.color ? { color: dto.color } : {}),
    localizedData: Object.fromEntries(
      Object.entries(localizedData).map(([language, value]) => [
        language,
        { name: value?.name ?? value?.code ?? '' },
      ]),
    ),
  }
}
